import { useCallback, useEffect, useRef, useState } from 'react';
import { getMachineOrigin } from '@/lib/api';
import { invalidateQueries, queryClient } from '@/lib/query-client';
import { integrationService, type HelpScoutConnectionStatus } from '@/services/integration.service';
import type { Integration } from '../types';
import {
	getHelpScoutErrorMessage,
	getHelpScoutRequestErrorMessage,
	isHelpScoutOAuthMessage,
	logHelpScoutRequestFailure
} from '../helpscout-oauth';

const POLL_INTERVAL_MS = 2000;

const initialStatus = (integration: Integration): HelpScoutConnectionStatus => ({
	status: integration.isConnected ? 'connected' : 'disconnected',
	companyName: integration.connectedName,
	webhookConfigured: false
});

interface UseHelpScoutConnectionResult {
	connection: HelpScoutConnectionStatus;
	errorMessage?: string;
	isAuthorizing: boolean;
	showSuccessCheckpoint: boolean;
	authorize: () => Promise<void>;
	refreshConnection: () => Promise<HelpScoutConnectionStatus | undefined>;
	dismissSuccessCheckpoint: () => void;
	markDisconnected: () => void;
}

export const useHelpScoutConnection = (integration: Integration): UseHelpScoutConnectionResult => {
	const [connection, setConnection] = useState<HelpScoutConnectionStatus>(() => initialStatus(integration));
	const [errorMessage, setErrorMessage] = useState<string>();
	const [isAuthorizing, setIsAuthorizing] = useState(false);
	const [showSuccessCheckpoint, setShowSuccessCheckpoint] = useState(false);
	const popupRef = useRef<Window | null>(null);
	const authorizationStartedRef = useRef(false);
	const didRefreshIntegrationsRef = useRef(false);

	const failAuthorization = useCallback((errorCode?: string, message = getHelpScoutErrorMessage(errorCode)): void => {
		authorizationStartedRef.current = false;
		setConnection((current) => ({ ...current, status: 'failed', errorCode }));
		setErrorMessage(message);
		setIsAuthorizing(false);
	}, []);

	const applyConnection = useCallback((nextConnection: HelpScoutConnectionStatus): void => {
		// 'connecting' is only meaningful for an attempt THIS session started. The
		// machine keeps its status in Redis for 15 minutes, so an abandoned attempt
		// (popup dismissed, tab closed) still reports 'connecting' long afterwards —
		// and adopting it renders the spinner screen with Authorize disabled and no
		// popup to close, stranding the sheet with no way to start over.
		if (nextConnection.status === 'connecting' && !authorizationStartedRef.current) {
			setConnection({ ...nextConnection, status: 'disconnected' });
			setIsAuthorizing(false);
			return;
		}
		setConnection(nextConnection);
		if (nextConnection.status === 'connected') {
			if (authorizationStartedRef.current && nextConnection.webhookConfigured) setShowSuccessCheckpoint(true);
			authorizationStartedRef.current = false;
			setErrorMessage(undefined);
			setIsAuthorizing(false);
			if (!didRefreshIntegrationsRef.current) {
				didRefreshIntegrationsRef.current = true;
				void invalidateQueries(['integrations']);
			}
		} else if (nextConnection.status === 'failed' || nextConnection.status === 'needs_attention') {
			authorizationStartedRef.current = false;
			setErrorMessage(getHelpScoutErrorMessage(nextConnection.errorCode));
			setIsAuthorizing(false);
		}
	}, []);

	const refreshConnection = useCallback(async (): Promise<HelpScoutConnectionStatus | undefined> => {
		try {
			const nextConnection = await queryClient.fetchQuery({
				queryKey: ['integrations', 'helpscout', 'status'],
				queryFn: integrationService.getHelpScoutConnectionStatus,
				staleTime: 0,
				retry: false
			});
			applyConnection(nextConnection);
			return nextConnection;
		} catch (error) {
			logHelpScoutRequestFailure('status', error);
			setConnection((current) => ({ ...current, status: 'failed' }));
			setErrorMessage(getHelpScoutRequestErrorMessage(error));
			setIsAuthorizing(false);
			return undefined;
		}
	}, [applyConnection]);

	// A DISCONNECTED card already knows its answer: the integrations list said so,
	// and `initialStatus` seeded from it. Asking the backend again before the agent
	// has clicked Authorize spends a request to learn nothing — and adopts whatever
	// the machine's 15-minute Redis status key still holds from an abandoned
	// attempt, which opened the sheet straight into a stale error or a permanent
	// "Connecting…". Only an already-connected integration needs the round trip, to
	// fill in the company, inboxes, and webhook health the list does not carry.
	useEffect(() => {
		if (!integration.isConnected) return;
		void refreshConnection();
	}, [integration.isConnected, refreshConnection]);

	// Poll while a connection attempt is in flight. The popup's postMessage is the
	// primary completion signal; this 2s interval is the fallback if it never arrives
	// (same shape as the other OAuth-popup providers).
	useEffect(() => {
		// Gate on THIS session's attempt, never on a server-reported 'connecting'.
		// The loop's only exit is the popup being closed, and a status the agent did
		// not start has no popup — `popupRef.current?.closed` is undefined, so it
		// polled every 2s forever with the Authorize button disabled.
		if (!isAuthorizing) return;
		const intervalId = window.setInterval(() => {
			void (async () => {
				const nextConnection = await refreshConnection();
				if (popupRef.current?.closed && (!nextConnection || nextConnection.status === 'disconnected')) {
					failAuthorization('popup_closed');
				}
			})();
		}, POLL_INTERVAL_MS);
		return () => window.clearInterval(intervalId);
	}, [failAuthorization, isAuthorizing, refreshConnection]);

	useEffect(() => {
		const handleMessage = (event: MessageEvent<unknown>): void => {
			if (!isHelpScoutOAuthMessage(event, popupRef.current, getMachineOrigin())) return;
			popupRef.current = null;
			if (event.data.status === 'failed') {
				failAuthorization(event.data.errorCode);
				return;
			}
			// Success carries only the outcome, not the full status (company, inboxes,
			// webhookConfigured), so fetch the authoritative connection once.
			setConnection((current) => ({ ...current, status: 'connecting' }));
			void refreshConnection();
		};

		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [failAuthorization, refreshConnection]);

	const authorize = async (): Promise<void> => {
		if (isAuthorizing) return;
		authorizationStartedRef.current = true;
		setIsAuthorizing(true);
		setErrorMessage(undefined);
		const popup = window.open(
			'about:blank',
			`helpscout_oauth_${Date.now()}_${crypto.randomUUID()}`,
			'popup=yes,width=600,height=720,resizable=yes,scrollbars=yes'
		);

		if (!popup) {
			failAuthorization('popup_blocked');
			return;
		}

		popupRef.current = popup;
		popup.focus();
		try {
			const authorization = await integrationService.authorizeHelpScout();
			const authorizationUrl = new URL(authorization.authorizationUrl);
			if (authorizationUrl.protocol !== 'https:') throw new Error('Help Scout authorization URL must use HTTPS');
			didRefreshIntegrationsRef.current = false;
			setConnection((current) => ({ ...current, status: 'connecting' }));
			popup.location.href = authorizationUrl.toString();
		} catch (error) {
			logHelpScoutRequestFailure('authorize', error);
			popup.close();
			popupRef.current = null;
			failAuthorization(undefined, getHelpScoutRequestErrorMessage(error));
		}
	};

	const markDisconnected = (): void => {
		setConnection({ status: 'disconnected', webhookConfigured: false });
		setErrorMessage(undefined);
	};

	return {
		connection,
		errorMessage,
		isAuthorizing,
		showSuccessCheckpoint,
		authorize,
		refreshConnection,
		dismissSuccessCheckpoint: () => setShowSuccessCheckpoint(false),
		markDisconnected
	};
};
